import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

export default function WebUiSettingsLacpPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [bonds, setBonds] = React.useState<
    Record<string, { lower_interfaces: string[] }>
  >({});
  const [ports, setPorts] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const data = await postWebUiApi<
      Record<string, { lower_interfaces: string[] }>
    >({
      service: 'vlan',
      access: 'get',
      command: 'get bonds',
    });
    const all = new Set<string>();
    Object.values(data ?? {}).forEach((b) =>
      (b.lower_interfaces ?? []).forEach((p) => all.add(p)),
    );
    if (!all.size)
      ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'].forEach((p) =>
        all.add(p),
      );
    setBonds(data ?? {});
    setPorts(Array.from(all).sort());
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const toggle = (port: string, bondName: string, checked: boolean) => {
    setBonds((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const list = new Set(next[k].lower_interfaces ?? []);
        if (k === bondName && checked) list.add(port);
        else list.delete(port);
        next[k] = { lower_interfaces: Array.from(list) };
      });
      return next;
    });
  };

  return (
    <WebUiPageWrapper titleList={['Settings', 'LACP']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-col gap-2">
        <h4 className="font-bold">LACP</h4>
        <table className="webui-table w-full">
          <thead>
            <tr className="webui-table-row-head">
              <th>Port</th>
              {Object.keys(bonds).map((name) => (
                <th key={name}>{name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ports.map((p) => (
              <tr key={p} className="webui-table-row-content">
                <td className="text-center">{p}</td>
                {Object.keys(bonds).map((name) => (
                  <td key={`${p}-${name}`} className="text-center">
                    <WebUiInput
                      type="checkbox"
                      disabled={readonly}
                      checked={Boolean(
                        bonds[name]?.lower_interfaces?.includes(p),
                      )}
                      onChange={(e) => toggle(p, name, e.target.checked)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="w-full">
          <WebUiButton
            disabled={readonly}
            onClick={async () => {
              try {
                await postWebUiApi({
                  service: 'vlan',
                  access: 'set',
                  command: 'set bonds',
                  parameters: bonds,
                });
                setMessage('Bonds updated.');
                setError(null);
                await reload();
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          >
            Set
          </WebUiButton>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
